/**
 * Location service for the location-based market (Jonathan 2026-05-21).
 *
 * The study laptop has no GPS, so the position is NOT taken from the browser's
 * HTML5 Geolocation API. Instead it is polled from a **local network endpoint**
 * (e.g. a phone app on the LAN sharing its position) — configured via the
 * `VITE_GPS_ENDPOINT` env var (default `http://localhost:8090/gps`), expected
 * to return `{ "lat": number, "lng": number }`. The coordinates are then
 * reverse-geocoded to a placename via OpenStreetMap **Nominatim**.
 *
 * Everything degrades gracefully: if the endpoint or Nominatim is unreachable
 * (offline, no phone), a sensible fallback location is used so the market
 * still renders. Resolution is cached for the session and de-duplicated.
 */

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
const GPS_ENDPOINT = env?.VITE_GPS_ENDPOINT ?? "http://localhost:8090/gps";
const NOMINATIM_REVERSE = "https://nominatim.openstreetmap.org/reverse";

export interface LocationInfo {
  lat: number;
  lng: number;
  /** Human-readable "City, CC" (or coordinates if geocoding failed). */
  placeName: string;
  /** Deterministic pseudo-bearing (deg) for the compass needle — there is no
   *  heading sensor, so it's derived from the coordinates. */
  bearingDeg: number;
  source: "endpoint" | "fallback";
}

const FALLBACK: LocationInfo = {
  lat: 47.3769,
  lng: 8.5417,
  placeName: "Bahnhofstrasse, Zürich",
  bearingDeg: 24,
  source: "fallback",
};

/** A fresh fix within this distance of the last geocoded one reuses the cached
 *  placename instead of re-querying Nominatim — so a stationary or jittering
 *  position never spends a look-up. */
export const REGEOCODE_MIN_MOVE_M = 30;

/** Equirectangular-approximation distance in metres between two lat/lng points.
 *  Accurate to well under a metre at the city-block scale this widget cares
 *  about, and far cheaper than a full haversine. */
export function metersBetween(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6_371_000;
  const toRad = Math.PI / 180;
  const meanLat = ((aLat + bLat) / 2) * toRad;
  const x = (bLng - aLng) * toRad * Math.cos(meanLat);
  const y = (bLat - aLat) * toRad;
  return Math.sqrt(x * x + y * y) * R;
}

/** Whether a fresh fix is far enough from the last *geocoded* one to warrant a
 *  new Nominatim look-up. A null or fallback `prev` always re-geocodes so the
 *  endpoint can take over once it comes online. */
export function shouldRegeocode(
  prev: Pick<LocationInfo, "lat" | "lng" | "source"> | null,
  lat: number,
  lng: number,
): boolean {
  if (!prev || prev.source !== "endpoint") return true;
  return metersBetween(prev.lat, prev.lng, lat, lng) > REGEOCODE_MIN_MOVE_M;
}

async function fetchJson(url: string, timeoutMs: number): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as unknown;
  } finally {
    clearTimeout(timer);
  }
}

function asNumber(v: unknown): number {
  return typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
}

export class LocationService {
  private static cached: LocationInfo | null = null;
  private static inflight: Promise<LocationInfo> | null = null;

  /** The endpoint configured for telemetry / debug display. */
  static get endpoint(): string {
    return GPS_ENDPOINT;
  }

  /** Last resolved location, if any (no fetch). */
  static current(): LocationInfo | null {
    return this.cached;
  }

  /** Resolve the location once per session (cached, de-duplicated). */
  static resolve(): Promise<LocationInfo> {
    if (this.cached) return Promise.resolve(this.cached);
    if (this.inflight) return this.inflight;
    this.inflight = this.doResolve().then((info) => {
      this.cached = info;
      this.inflight = null;
      return info;
    });
    return this.inflight;
  }

  /**
   * Re-poll the GPS endpoint for a fresh fix (bypasses the resolve() cache so
   * the market can follow a moving position). The placename is only re-geocoded
   * when the fix has moved more than {@link REGEOCODE_MIN_MOVE_M} from the last
   * geocoded one — a near-identical fix returns the cached location untouched,
   * with no Nominatim look-up. A failed poll keeps the last known fix.
   */
  static async refresh(): Promise<LocationInfo> {
    try {
      const fix = (await fetchJson(GPS_ENDPOINT, 2500)) as Record<string, unknown>;
      const lat = asNumber(fix.lat ?? fix.latitude);
      const lng = asNumber(fix.lng ?? fix.lon ?? fix.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error("bad fix");
      if (!shouldRegeocode(this.cached, lat, lng)) return this.cached as LocationInfo;
      const placeName = await this.reverseGeocode(lat, lng);
      const info: LocationInfo = {
        lat,
        lng,
        placeName,
        bearingDeg: this.pseudoBearing(lat, lng),
        source: "endpoint",
      };
      this.cached = info;
      return info;
    } catch {
      return this.cached ?? FALLBACK;
    }
  }

  private static async doResolve(): Promise<LocationInfo> {
    try {
      const fix = (await fetchJson(GPS_ENDPOINT, 2500)) as Record<string, unknown>;
      const lat = asNumber(fix.lat ?? fix.latitude);
      const lng = asNumber(fix.lng ?? fix.lon ?? fix.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error("bad fix");
      const placeName = await this.reverseGeocode(lat, lng);
      return { lat, lng, placeName, bearingDeg: this.pseudoBearing(lat, lng), source: "endpoint" };
    } catch {
      return FALLBACK;
    }
  }

  private static async reverseGeocode(lat: number, lng: number): Promise<string> {
    try {
      // zoom=18 asks Nominatim for building/street-level detail so the address
      // block carries road / neighbourhood fields, not just the city.
      const url = `${NOMINATIM_REVERSE}?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
      const j = (await fetchJson(url, 3000)) as {
        address?: Record<string, string>;
        name?: string;
        display_name?: string;
      };
      const a = j.address ?? {};
      // Finest meaningful label first (street / neighbourhood), then a coarser
      // locality for context — e.g. "Bahnhofstrasse, Zürich" / "Seefeld, Zürich".
      const fine =
        a.road ?? a.pedestrian ?? a.footway ?? a.neighbourhood ?? a.quarter ?? a.suburb ?? a.hamlet ?? a.city_district ?? "";
      const coarse = a.suburb ?? a.city ?? a.town ?? a.village ?? a.municipality ?? a.county ?? "";
      const parts = [fine, coarse].filter((p, i, arr) => p !== "" && arr.indexOf(p) === i);
      const label =
        parts.join(", ") ||
        j.name ||
        (j.display_name ? j.display_name.split(",").slice(0, 2).join(",").trim() : "");
      return label ? this.cap(label, 26) : `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
    } catch {
      return `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
    }
  }

  /** Trim an over-long placename so it fits the compact compass widget. */
  private static cap(s: string, n: number): string {
    return s.length > n ? `${s.slice(0, n - 1)}…` : s;
  }

  /** Stable bearing from coordinates so the compass needle is consistent. */
  private static pseudoBearing(lat: number, lng: number): number {
    return Math.round(Math.abs(lat * 1000 + lng * 1000) % 360);
  }
}
